import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useBroadcastPreviewUsers } from "../hooks/use-broadcast";
import { BroadcastFilters } from "../services/broadcast";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: BroadcastFilters;
}

export function RecipientsPreviewDialog({ open, onOpenChange, filters }: Props) {
  const [page, setPage] = useState(0);

  // Reset to the first page whenever the dialog opens or the audience changes.
  useEffect(() => {
    setPage(0);
  }, [open, filters]);

  const { data, isFetching } = useBroadcastPreviewUsers(filters, page, open);

  const limit = data?.limit ?? 100;
  const total = data?.matchedUsers ?? 0;
  const count = data?.users.length ?? 0;
  const start = total === 0 ? 0 : page * limit + 1;
  const end = page * limit + count;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Audience preview</DialogTitle>
        </DialogHeader>

        {!data && isFetching ? (
          <p className="py-6 text-center text-sm text-gray-400">
            Loading recipients…
          </p>
        ) : !data || total === 0 ? (
          <p className="py-6 text-center text-sm text-gray-400">
            No matching users.
          </p>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-gray-500">
              Showing{" "}
              <span className="font-medium text-gray-800">
                {start.toLocaleString()}–{end.toLocaleString()}
              </span>{" "}
              of{" "}
              <span className="font-medium text-gray-800">
                {total.toLocaleString()}
              </span>{" "}
              matching users.
            </p>

            <div className="max-h-[50vh] overflow-auto rounded-md border border-gray-100">
              <table className="w-full text-left text-sm">
                <thead className="sticky top-0 bg-gray-50">
                  <tr className="text-xs uppercase tracking-wide text-gray-400">
                    <th className="px-3 py-2 font-medium">Name</th>
                    <th className="px-3 py-2 font-medium">Phone</th>
                    <th className="px-3 py-2 font-medium">Email</th>
                  </tr>
                </thead>
                <tbody>
                  {data.users.map((u) => (
                    <tr key={u.id} className="border-t border-gray-50">
                      <td className="px-3 py-2 text-gray-800">
                        {u.full_name || "—"}
                      </td>
                      <td className="px-3 py-2 text-gray-600">
                        {u.phone || "—"}
                      </td>
                      <td className="px-3 py-2 text-gray-600">
                        {u.email || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between pt-1">
              <span className="text-xs text-gray-400">
                Page {page + 1} of {Math.max(1, Math.ceil(total / limit))}
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page === 0 || isFetching}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!data.hasMore || isFetching}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
